/**
 * Smart prompts — deterministic, data-derived prompt chips for the
 * Dashboard header.
 *
 * Turns three live signals the Dashboard already computes (overdue
 * follow-ups from `getFollowUpReminders`, high-severity `scanSystem`
 * findings, slipping customers from `rankCustomersByRisk`) into at most
 * `MAX_SIGNAL_PROMPTS` urgent chips, and ALWAYS appends the standing
 * "Ask Argo: what changed this week?" chip. Each chip either deep-links
 * to the view that owns the signal (`kind: 'navigate'`) or hands a
 * prefilled question to Chat (`kind: 'ask'`).
 *
 * PURE by contract: counts in, chips out — no store reads, no clock, no
 * fabrication. Zero signals honestly yields just the single standing chip.
 */

/** Views a smart prompt may deep-link to — a subset of the app's ViewKey. */
export type SmartPromptView = 'contacts' | 'customer-success';

export type SmartPrompt =
  | {
      id: string;
      kind: 'navigate';
      /** Chip label, e.g. "3 follow-ups overdue — triage now". */
      label: string;
      view: SmartPromptView;
    }
  | {
      id: string;
      kind: 'ask';
      label: string;
      /** The question handed to Chat, prefilled. */
      query: string;
    };

/** Live counts the Dashboard already derives — never estimated. */
export interface SmartPromptSignals {
  /** Contacts whose follow-up date has fully passed. */
  overdueFollowUps: number;
  /** High-severity system-scanner findings. */
  highFindings: number;
  /** Live customers outside the healthy band. */
  atRiskCustomers: number;
}

/** Cap on signal-derived chips, keeping the header row to 2–3 chips total. */
export const MAX_SIGNAL_PROMPTS = 2;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * Derive the header chips: signal chips in fixed priority order
 * (follow-ups → high findings → slipping customers), capped at
 * `MAX_SIGNAL_PROMPTS`, then the standing weekly-review ask chip.
 * Non-positive counts contribute nothing.
 */
export function deriveSmartPrompts(signals: SmartPromptSignals): SmartPrompt[] {
  const chips: SmartPrompt[] = [];

  if (signals.overdueFollowUps > 0) {
    chips.push({
      id: 'followups-overdue',
      kind: 'navigate',
      label: `${plural(signals.overdueFollowUps, 'follow-up')} overdue — triage now`,
      view: 'contacts',
    });
  }
  if (signals.highFindings > 0) {
    chips.push({
      id: 'high-findings',
      kind: 'ask',
      label: `${plural(signals.highFindings, 'high-risk finding')} — ask Argo where to start`,
      query: `We have ${plural(signals.highFindings, 'high-severity finding')} right now. Which should I fix first, and why?`,
    });
  }
  if (signals.atRiskCustomers > 0) {
    chips.push({
      id: 'customers-slipping',
      kind: 'navigate',
      label: `${plural(signals.atRiskCustomers, 'customer')} slipping — review health`,
      view: 'customer-success',
    });
  }

  return [
    ...chips.slice(0, MAX_SIGNAL_PROMPTS),
    {
      id: 'week-review',
      kind: 'ask',
      label: 'Ask Argo: what changed this week?',
      query: 'What changed this week across accounts, tickets, and follow-ups?',
    },
  ];
}
